import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import SurveyService from "../../lib/SurveyService";
import { Loader, Info } from "lucide-react";
import { OwnliButton } from "../ownli/OwnliScreenWrapper";
import track from "../../lib/analytics/events";

const SURVEY_CACHE_KEY = "alarmSurvey_answers";
const SURVEY_HISTORY_KEY = "alarmSurvey_history";

const saveAnswersToCache = (answers) => {
  try {
    sessionStorage.setItem(SURVEY_CACHE_KEY, JSON.stringify(answers));
  } catch (error) {
    console.error("Failed to cache survey answers:", error);
  }
};

// Load answers from cache
const loadAnswersFromCache = () => {
  try {
    const cached = sessionStorage.getItem(SURVEY_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error("Failed to load cached survey answers:", error);
    return {};
  }
};

// Save question history to cache
const saveHistoryToCache = (history) => {
  try {
    sessionStorage.setItem(SURVEY_HISTORY_KEY, JSON.stringify(history));
  } catch (error) {
    console.error("Failed to cache survey history:", error);
  }
};

// Load question history from cache
const loadHistoryFromCache = () => {
  try {
    const cached = sessionStorage.getItem(SURVEY_HISTORY_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error("Failed to load cached survey history:", error);
    return null;
  }
};

// Clear survey cache
const clearSurveyCache = () => {
  try {
    sessionStorage.removeItem(SURVEY_CACHE_KEY);
    sessionStorage.removeItem(SURVEY_HISTORY_KEY);
  } catch (error) {
    console.error("Failed to clear survey cache:", error);
  }
};

const AlarmSurvey = ({ onComplete, onBack }) => {
  const { getIdToken, user } = useAuth();
  const surveyId = "STEM_NationwideHomeAlarm";

  const [surveyData, setSurveyData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [previousResponses, setPreviousResponses] = useState(null);

  useEffect(() => {
    if (!isReadOnly && Object.keys(answers).length > 0) {
      saveAnswersToCache(answers);
    }
  }, [answers, isReadOnly]);

  // Auto-save question history to cache
  useEffect(() => {
    if (!isReadOnly && questionHistory.length > 0) {
      saveHistoryToCache(questionHistory);
    }
  }, [questionHistory, isReadOnly]);

  // Fetch survey template
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setIsLoading(true);
        const token = await getIdToken();

        let prevResponses = await checkPreviousResponses(token);
        if (prevResponses.hasPreviousResponse) {
          setIsReadOnly(true);
        }

        const survey = await SurveyService.getSurveyById(surveyId, token);
        setSurveyData(survey);

        if (survey && survey.questions && survey.questions.length > 0) {
          if (!prevResponses.hasPreviousResponse) {
            track.surveyStarted({
              survey_id: surveyId,
              question_count: survey.questions.length,
            });
          }

          const sortedQuestions = [...survey.questions].sort(
            (a, b) => a.displayOrder - b.displayOrder
          );
          const firstQuestionId = sortedQuestions[0].id;

          // Load cached history or use first question
          const cachedHistory = loadHistoryFromCache();
          if (
            cachedHistory &&
            cachedHistory.length > 0 &&
            !prevResponses.hasPreviousResponse
          ) {
            //setQuestionHistory(cachedHistory);
            //setCurrentQuestionId(cachedHistory[cachedHistory.length - 1]);
            setQuestionHistory([firstQuestionId]);
            setCurrentQuestionId(firstQuestionId);
          } else {
            setCurrentQuestionId(firstQuestionId);
            setQuestionHistory([firstQuestionId]);
          }
        }

        if (
          prevResponses.hasPreviousResponse &&
          prevResponses.previousResponses &&
          survey
        ) {
          const convertedAnswers = convertResponsesFormatToAnswers(
            prevResponses.previousResponses,
            survey
          );
          setAnswers(convertedAnswers);
        } else {
          // Load cached answers if no server response
          const cachedAnswers = loadAnswersFromCache();
          if (Object.keys(cachedAnswers).length > 0) {
            setAnswers(cachedAnswers);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading survey:", error);
        setIsLoading(false);
      }
    };

    if (user) {
      fetchSurvey();
    }
  }, [surveyId, user]);

  const handleAnswer = (questionId, value, answerId, isMultiSelect = false) => {
    if (isReadOnly) return;

    if (isMultiSelect) {
      const currentValues = answers[questionId]?.values || [];
      const currentIds = answers[questionId]?.answerIds || [];
      const isSelected = currentValues.includes(value);

      setAnswers({
        ...answers,
        [questionId]: {
          values: isSelected
            ? currentValues.filter((v) => v !== value)
            : [...currentValues, value],
          answerIds: isSelected
            ? currentIds.filter((id) => id !== answerId)
            : [...currentIds, answerId],
          multiSelect: true,
        },
      });
    } else {
      const currentOtherAnswer = answers[questionId]?.otherAnswer || "";
      setAnswers({
        ...answers,
        [questionId]: {
          value,
          answerId,
          multiSelect: false,
          ...(currentOtherAnswer ? { otherAnswer: currentOtherAnswer } : {}),
        },
      });
    }
  };

  const handleOtherTextChange = (questionId, otherText) => {
    setAnswers({
      ...answers,
      [questionId]: {
        ...answers[questionId],
        otherAnswer: otherText,
      },
    });
  };

  const isAnswered = (question) => {
    if (!question) return false;
    const answer = answers[question.id];
    if (!answer) return false;

    if (
      (question.allowOther || question.imageChoice) &&
      answer.value === "other" &&
      (!answer.otherAnswer || answer.otherAnswer.trim() === "")
    ) {
      return false;
    }

    if (question.multiSelect) {
      return answer.values && answer.values.length > 0;
    }
    return !!answer.value;
  };

  const handleNext = () => {
    const currentQuestion = getCurrentQuestion();
    const currentAnswer = answers[currentQuestion.id];

    const selectedOption = currentQuestion.options?.find(
      (opt) => opt.value === currentAnswer?.value
    );

    let nextQuestionId = null;

    if (selectedOption?.nextQuestionId) {
      nextQuestionId = selectedOption.nextQuestionId;
    } else if (currentQuestion.nextQuestionId) {
      nextQuestionId = currentQuestion.nextQuestionId;
    } else {
      const sortedQuestions = surveyData.questions.sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      const currentIndex = sortedQuestions.findIndex(
        (q) => q.id === currentQuestion.id
      );

      if (currentIndex < sortedQuestions.length - 1) {
        nextQuestionId = sortedQuestions[currentIndex + 1].id;
      }
    }

    track.surveyQuestionAnswered({
      survey_id: surveyId,
      question_id: currentQuestion.id,
      question_number: questionHistory.length,
      has_answer: Boolean(currentAnswer?.value || currentAnswer?.values?.length),
    });

    if (nextQuestionId) {
      setQuestionHistory([...questionHistory, nextQuestionId]);
      setCurrentQuestionId(nextQuestionId);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (questionHistory.length > 1) {
      const newHistory = [...questionHistory];
      newHistory.pop();
      const previousQuestionId = newHistory[newHistory.length - 1];

      setQuestionHistory(newHistory);
      setCurrentQuestionId(previousQuestionId);
    }
  };

  const handleComplete = async () => {
    if (isReadOnly) {
      if (onComplete) {
        onComplete({
          responses: previousResponses.responses,
          dateCompleted: previousResponses.dateCompleted,
          readOnly: true,
        });
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getIdToken();

      const formattedResponses = Object.entries(answers).map(
        ([questionId, answer]) => {
          if (answer.multiSelect) {
            return {
              type: "CHOICE",
              questionId,
              selections: answer.values.map((value, index) => ({
                choiceOptionId: answer.answerIds[index],
                value: value,
              })),
              otherAnswer: answer.otherAnswer || "",
            };
          } else if (answer.value === "other") {
            return {
              type: "CHOICE",
              questionId,
              selections: [],
              otherAnswer: answer.otherAnswer || "",
            };
          } else {
            return {
              type: "CHOICE",
              questionId,
              selections: [
                { choiceOptionId: answer.answerId, value: answer.value },
              ],
              otherAnswer: "",
            };
          }
        }
      );

      await SurveyService.submitSurveyResponses(
        surveyId,
        user.uid,
        formattedResponses,
        token
      );
      clearSurveyCache();

      track.surveySubmitted({
        survey_id: surveyId,
        response_count: formattedResponses.length,
      });

      if (onComplete) {
        onComplete({ responses: formattedResponses });
      }
    } catch (error) {
      console.error("Error submitting survey:", error);
      track.errorShown({
        type: "survey_submit_failed",
        screen: "alarm_survey",
        message: error?.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkPreviousResponses = async (token) => {
    try {
      if (!token) return { hasPreviousResponse: false };

      const responseData = await SurveyService.getSurveyResponsesBySurveyId(
        surveyId,
        token
      );

      if (responseData && responseData.dateCompleted) {
        setPreviousResponses(responseData);
        return { hasPreviousResponse: true, previousResponses: responseData };
      }

      return { hasPreviousResponse: false };
    } catch (err) {
      console.log("No previous responses found or error checking:", err);
      return { hasPreviousResponse: false };
    }
  };

  const convertResponsesFormatToAnswers = (responseData, surveyTemplate) => {
    if (!responseData || !responseData.responses || !surveyTemplate) {
      return {};
    }

    const convertedAnswers = {};

    responseData.responses.forEach((response) => {
      const question = surveyTemplate.questions.find(
        (q) => q.id === response.questionId
      );
      if (!question) return;

      const questionId = response.questionId;

      switch (response.responseType) {
        case "TEXT":
          convertedAnswers[questionId] = {
            value: response.answer || "",
          };
          break;

        case "CHOICE":
          if (question.multiSelect) {
            const values = response.selections.map(
              (s) => s.optionValue || s.value
            );
            const answerIds = response.selections.map(
              (s) => s.choiceOptionId || s.id
            );

            convertedAnswers[questionId] = {
              values,
              answerIds,
              multiSelect: true,
            };
          } else {
            if (response.selections && response.selections.length > 0) {
              const answer = {
                value:
                  response.selections[0].optionValue ||
                  response.selections[0].value,
                answerId:
                  response.selections[0].choiceOptionId ||
                  response.selections[0].id,
                multiSelect: false,
              };
              if (response.otherAnswer) {
                answer.otherAnswer = response.otherAnswer;
              }
              convertedAnswers[questionId] = answer;
            } else if (response.otherAnswer) {
              convertedAnswers[questionId] = {
                value: "other",
                answerId: "other",
                otherAnswer: response.otherAnswer,
                multiSelect: false,
              };
            }
          }
          break;

        default:
          console.warn(`Unsupported response type: ${response.responseType}`);
      }
    });

    return convertedAnswers;
  };

  const getCurrentQuestion = () => {
    if (!surveyData || !currentQuestionId) return null;
    return surveyData.questions.find((q) => q.id === currentQuestionId);
  };

  // RENDER METHODS

  const renderBackButton = () => {
    if (questionHistory.length > 1) {
      return (
        <button
          onClick={handlePrevious}
          disabled={isReadOnly}
          className={`w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors ${
            isReadOnly ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
          }`}
        >
          Back
        </button>
      );
    }

    if (onBack && questionHistory.length === 1) {
      return (
        <button
          onClick={onBack}
          className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors hover:bg-gray-50"
        >
          Back
        </button>
      );
    }

    return null;
  };

  const renderReadOnlyBanner = () => {
    if (!isReadOnly || !previousResponses) return null;

    return (
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              This survey was completed on{" "}
              {new Date(previousResponses.dateCompleted).toLocaleDateString()}.
              Review your responses below.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderImageChoice = (question) => {
    const isOtherSelected = answers[question.id]?.value === "other";

    return (
      <>
        <div className="grid grid-cols-2 gap-4">
          {question.options
            ?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .map((option) => {
              const isSelected = question.multiSelect
                ? answers[question.id]?.values?.includes(option.value)
                : answers[question.id]?.value === option.value;

              return (
                <button
                  key={option.id}
                  onClick={() =>
                    handleAnswer(
                      question.id,
                      option.value,
                      option.id,
                      question.multiSelect
                    )
                  }
                  disabled={isReadOnly}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? "border-crimsonGlory bg-crimsonGlory-50"
                      : isReadOnly
                      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                      : "border-gray-200 hover:border-purple-300 hover:shadow-md"
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-crimsonGlory rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}

                  <div className="flex flex-col items-center justify-center space-y-3">
                    {option.imageUrl && (
                      <img
                        src={option.imageUrl}
                        alt={option.altText || option.value}
                        className="w-full h-24 object-contain"
                        onError={(e) => {
                          console.error("Failed to load image:", option.imageUrl);
                          e.target.style.display = "none";
                        }}
                      />
                    )}

                    <span
                      className={`text-sm font-medium text-center ${
                        isSelected ? "text-crimsonGlory" : "text-gray-700"
                      }`}
                    >
                      {option.value}
                    </span>
                  </div>
                </button>
              );
            })}

          {/* "Other" option tile */}
          <button
            onClick={() =>
              handleAnswer(question.id, "other", "other", false)
            }
            disabled={isReadOnly}
            className={`relative p-4 rounded-lg border-2 transition-all ${
              isOtherSelected
                ? "border-crimsonGlory bg-crimsonGlory-50"
                : isReadOnly
                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                : "border-gray-200 hover:border-purple-300 hover:shadow-md"
            }`}
          >
            {isOtherSelected && (
              <div className="absolute top-2 right-2 w-6 h-6 bg-crimsonGlory rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}

            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="w-full h-24 flex items-center justify-center text-gray-400">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span
                className={`text-sm font-medium text-center ${
                  isOtherSelected ? "text-crimsonGlory" : "text-gray-700"
                }`}
              >
                Other
              </span>
            </div>
          </button>
        </div>

        {/* Text input when "Other" is selected */}
        {isOtherSelected && (
          <input
            type="text"
            value={answers[question.id]?.otherAnswer || ""}
            onChange={(e) => handleOtherTextChange(question.id, e.target.value)}
            placeholder="Please specify your alarm provider"
            className="w-full mt-4 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        )}
      </>
    );
  };

  const renderTextChoice = (question) => {
    return (
      <div className="space-y-3">
        {question.options
          ?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .map((option) => {
            const isSelected = question.multiSelect
              ? answers[question.id]?.values?.includes(option.value)
              : answers[question.id]?.value === option.value;

            return (
              <button
                key={option.id}
                onClick={() =>
                  handleAnswer(
                    question.id,
                    option.value,
                    option.id,
                    question.multiSelect
                  )
                }
                disabled={isReadOnly}
                className={`w-full p-4 text-left rounded-lg border transition-all ${
                  isSelected
                    ? "border-crimsonGlory bg-crimsonGlory-200 text-purple-900"
                    : isReadOnly
                    ? "border-gray-200 bg-gray-50 text-gray-700 cursor-not-allowed"
                    : "border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  {question.multiSelect && (
                    <div
                      className={`w-4 h-4 rounded border-2 transition-colors ${
                        isSelected
                          ? "bg-crimsonGlory border-crimsonGlory"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  )}
                  <span>{option.text}</span>
                </div>
              </button>
            );
          })}

        {question.allowOther && answers[question.id]?.value === "other" && (
          <input
            type="text"
            value={answers[question.id]?.otherAnswer || ""}
            onChange={(e) => handleOtherTextChange(question.id, e.target.value)}
            placeholder="Please specify"
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        )}
      </div>
    );
  };

  const renderInstructionQuestion = (question) => {
    return (
      <div className="p-4 space-y-6 stagger-children">
        <div className="space-y-4">
          {question.mediaType === "IMAGE" && question.mediaUrl && (
            <div className="flex justify-center">
              {!imgLoaded && (
                <div className="flex flex-col items-center justify-center p-8">
                  <Loader className="w-8 h-8 animate-spin text-crimsonGlory mb-2" />
                  <span className="text-sm text-gray-600">
                    Loading image...
                  </span>
                </div>
              )}

              <img
                src={question.mediaUrl}
                alt="Instruction visual"
                className={`max-w-full h-auto rounded-lg shadow-sm ${
                  imgLoaded ? "block" : "hidden"
                }`}
                onLoad={() => setImgLoaded(true)}
                onError={(e) => {
                  console.error(
                    "Failed to load instruction image:",
                    question.mediaUrl
                  );
                  setImgLoaded(true);
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {(!question.mediaType || question.mediaType !== "IMAGE") &&
            question.instructionText && (
              <div className="prose prose-sm max-w-none">
                <div
                  className="text-center"
                  dangerouslySetInnerHTML={{
                    __html: question.instructionText.replace(/\n/g, "<br/>"),
                  }}
                />
              </div>
            )}

          <div className="flex flex-col gap-3">
            {question.clickTextPrimary && (
              <OwnliButton
                onClick={() => {
                  if (question.clickUrlPrimary === "completeSurvey") {
                    setIsSubmitting(true);
                    handleComplete();
                  } else if (question.clickUrlPrimary) {
                    window.open(question.clickUrlPrimary, "_blank");
                  } else {
                    handleNext();
                  }
                }}
                disabled={isSubmitting}
                className={`w-full flex uppercase font-bold items-center justify-center gap-2 text-white px-4 py-3 rounded-lg transition-colors ${
                  isSubmitting
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-crimsonGlory hover:bg-crimsonGloryDark"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>{question.clickTextPrimary}</span>
                )}
              </OwnliButton>
            )}

            {question.clickTextSecondary && (
              <OwnliButton
                onClick={() => {
                  if (question.clickUrlSecondary) {
                    window.open(question.clickUrlSecondary, "_blank");
                  } else {
                    handleNext();
                  }
                }}
                className="text-center text-gray-600 hover:text-gray-900 text-sm transition-colors"
              >
                {question.clickTextSecondary}
              </OwnliButton>
            )}

            {!question.clickTextPrimary && !question.clickTextSecondary && (
              <OwnliButton
                onClick={handleNext}
                className="w-full flex uppercase font-bold items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors bg-crimsonGlory text-white hover:bg-crimsonGloryDark"
              >
                Continue
              </OwnliButton>
            )}

            {renderBackButton()}
          </div>
        </div>
      </div>
    );
  };

  const renderChoiceQuestion = (question) => {
    return (
      <div className="p-4 space-y-6 stagger-children">
        {renderReadOnlyBanner()}

        <div className="space-y-6">
          <h2 className="text-lg font-bold text-gray-900">{question.prompt}</h2>
          {question.helpText && (
            <p className="text-sm text-gray-600">{question.helpText}</p>
          )}

          {question.imageChoice
            ? renderImageChoice(question)
            : renderTextChoice(question)}

          <button
            onClick={handleNext}
            disabled={!isAnswered(question) || isSubmitting}
            className={`w-full flex items-center justify-center gap-2 uppercase font-bold px-4 py-3 rounded-lg transition-colors ${
              isAnswered(question) && !isSubmitting
                ? "bg-crimsonGlory text-white hover:bg-crimsonGloryDark"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Next"
            )}
          </button>

          {renderBackButton()}
        </div>
      </div>
    );
  };

  // MAIN RENDER

  if (isLoading) {
    return (
      <div className="p-4 space-y-6 stagger-children">
        <div className="flex flex-col items-center justify-center p-8">
          <Loader className="w-8 h-8 animate-spin text-crimsonGlory mb-4" />
          <span className="text-gray-600">Loading survey...</span>
        </div>
      </div>
    );
  }

  const question = getCurrentQuestion();
  if (!question) return null;

  if (question.type === "INSTRUCTION") {
    return renderInstructionQuestion(question);
  }

  return renderChoiceQuestion(question);
};

export default AlarmSurvey;
